import "components/taskdetails-remake/style/taskdetail.scss";
import "components/taskdetails-remake/style/taskDetailsTemplates.scss";
import "components/taskdetails-remake/style/grid.scss";
import "components/taskdetails-remake/style/task_details.scss";
import "react-resizable/css/styles.css";
import "./style/taskDetails.css";
import {forwardRef, useEffect, useReducer, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {ResizableBox} from "react-resizable";
import {
    CONFIRMATION, DETAILS_VIEW_TYPE, DOCUMENT_OVERVIEW_STATUS, LOADING_STATUS,
    WORKFLOW_DETAILS, DOCUMENT_DETAILS, DOCUMENT_PREVIEW, COMMENTS, EDITOR, ACCOUNTING_GRID,
    PANELS_BY_DOCTYPE_AND_INTEGRATION, DOCUMENT_TYPE, TASK_ACTIONS, DETAILS_STATUS, DOCUMENT_PREVIEW_STATUS,
    MODAL_ACTIONS,
} from "utils/constants";
import {canDo, keyPressHandler} from "utils/keyPressHandler.function";
import {snowplowTrackEvent} from "utils/snowplow";
import {SaveDiscardMessage} from "utils/confirmMoreMessages";
import {useModalContext} from "components/modals/ModalContext";
import {useTranslation} from "components/translations/useTranslation";
import * as reduxSelectors from "components/store/application.reducers";
import {approveMultipleShortcutKey, loadTasks} from "components/mytasks/myTasks.action";
import {changeTryToLoopTasksFlag} from "components/mytasks/action/taskAction.action";
import {eventBus} from "components/pagelayout/EventBus";
import {Tooltip} from "components/tooltip/Tooltip";
import {SHARED_TOOLTIP_ID} from "components/tooltip/AppTooltip.component";
import {PanelErrorBoundary} from "components/errorpage/PanelErrorBoundary";
import {
    documentPreviewTitleChanged,
    documentPreviewStatusChanged,
    editorTitleChanged,
    editorReattach,
    taskDetailsClosing,
    selectedDocumentChanged,
} from "components/taskdetails-remake/taskDetails.action.js";
import AttachmentsPanel from "components/taskdetails-remake/panels/attachments/AttachmentsPanel.component";
import DocumentEditorPanel from "./panels/editor/DocumentEditorPanel.component";
import AccountingGridPanel from "./panels/editor/AccountingGridPanel.component";
import {sendEditorCommand} from "./panels/editor/editorCommands";
import DocumentPreviewWindow from "components/taskdetails-remake/panels/attachments/detached/DocumentPreviewWindow.component";
import DocumentEditorWindow from "./panels/editor/detached/DocumentEditorWindow.component";
import AsyncTaskActionExecutorTask from "./AsyncTaskActionExecutor.component";
import TaskHeader from "./navigation/TaskHeader.component";
import DocumentData from "./panels/documentdata/DocumentData.component";
import DraftAndComments from "./panels/draftcomments/DraftAndComments.component";
import WorkflowDetailsPanel from "./panels/workflowdetails/WorkflowDetailsPanel.component";
import type {TaskDetailsProps, TaskDetailsResizeHandleProps} from "types/taskDetails";

const LEFT_SIDE_MIN_WIDTH = 650;
const MIN_SCREEN_WIDTH_FOR_RESIZE = 1300;
const SPACE_BETWEEN_PANELS = 16;
const EDITOR_PORTAL_STORAGE_KEY = "@@grid/editor/portal";
const ATTACHMENTS_WIDTH_STORAGE_KEY = "@@grid/attachments-width";

const HandledTaskTrigger = ({closeCallback}: { closeCallback: () => void }) => {
    const {toggleModal} = useModalContext() || {};
    const closeCallbackRef = useRef(closeCallback);
    closeCallbackRef.current = closeCallback;

    useEffect(() => {
        toggleModal?.(MODAL_ACTIONS.HANDLED_TASK, {closeCallback: () => closeCallbackRef.current?.()});
    }, [toggleModal]);

    return null;
};

const NavigationConfirmTrigger = ({
                                      translate,
                                      iframeHasUnsavedChanges,
                                      enableSaveDiscard,
                                      remain,
                                      saveChanges,
                                      discardChanges,
                                  }) => {
    const {toggleModal} = useModalContext() || {};
    const callbacksRef = useRef({remain, saveChanges, discardChanges});
    callbacksRef.current = {remain, saveChanges, discardChanges};

    useEffect(() => {
        toggleModal?.(MODAL_ACTIONS.CONFIRM_ACTION, {
            hideClose: true,
            confirmButtonText: "popUp.confirm.gotit",
            confirmButtonColor: "btn-default",
            enableSaveDiscard: enableSaveDiscard,
            isHandleMultipleActions: false,
            moreContent: (
                <SaveDiscardMessage translate={translate}
                                    iframeHasUnsavedChanges={iframeHasUnsavedChanges}/>
            ),
            handleAction: () => {
                toggleModal(null);
                callbacksRef.current.remain?.();
            },
            saveCallback: () => callbacksRef.current.saveChanges?.(),
            discardCallback: () => callbacksRef.current.discardChanges?.(),
            closeCallback: () => callbacksRef.current.remain?.(),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- open once on mount
    }, []);

    return null;
};

const ResizeHandle = forwardRef<HTMLDivElement, TaskDetailsResizeHandleProps>(
    ({handleAxis, height, translate, ...props}, ref) => (
        <div ref={ref} className={`handle-resize-${handleAxis}`} {...props}
             style={{height: height - 10 + "px"}}>
            <div className={"resize-handle-blue"} data-tip="resize-handle-tooltip" data-for={SHARED_TOOLTIP_ID}/>
            <Tooltip id="resize-handle-tooltip" content={() => translate("taskDetail.dragToResize")}/>
        </div>
    ));

const readEditorInPortal = () =>
    Boolean(window.localStorage.getItem(EDITOR_PORTAL_STORAGE_KEY)) && window.localStorage.getItem(EDITOR_PORTAL_STORAGE_KEY) !== "false";

const getEditorSizeClass = (editorVisibleInline: unknown, renderEditorInPortal: boolean) => {
    if (!editorVisibleInline) return "";
    return renderEditorInPortal ? " editor-small" : " editor-large";
};

// the grid templates of taskdetails-remake/style/taskDetailsTemplates.scss are named after the panels they show
const getGridContainerClass = (panelsVisible: Record<string, unknown>, editorVisibleInline: unknown, renderEditorInPortal: boolean) =>
    "grid"
    + (panelsVisible[WORKFLOW_DETAILS] ? "_workflow" : "")
    + (panelsVisible[DOCUMENT_DETAILS] ? "_data" : "")
    + (panelsVisible[DOCUMENT_PREVIEW] ? "_preview" : "")
    + (panelsVisible[COMMENTS] ? "_comments" : "")
    + getEditorSizeClass(editorVisibleInline, renderEditorInPortal);

/**
 * The redesigned task details page, behind fe-task-details-redesign. The classic page it replaces is
 * taskdetails-remake/TaskDetails.component; the panels are still the classic ones, and are remade one
 * by one in the modules of the new design.
 *
 * Unlike the classic page there is no sidebar: every panel the integration offers is shown, and
 * hiding, detaching and moving them will be done from the modules themselves.
 */
export const TaskDetails = (props: TaskDetailsProps) => {
    const {translate, translationKeyExists} = useTranslation();
    const dispatch = useDispatch();

    const workflowData = useSelector(reduxSelectors.getTaskWorkflowDetails);
    const userLanguage = useSelector(reduxSelectors.getLanguage);
    const documentData = useSelector(reduxSelectors.getTaskDocumentData);
    const taskActionsRunning = useSelector(reduxSelectors.areTaskActionsRunning);
    const actionParams = useSelector(reduxSelectors.getActionParameters);
    const userConfiguration = useSelector(reduxSelectors.getUsersData);
    const commentsCount = useSelector(reduxSelectors.getTaskDraftCommentsCount);
    const iframeHasUnsavedChanges = useSelector(reduxSelectors.getIframeHasUnsavedChanges);
    const iframeVBNXTSaveDiscardFlag = useSelector(reduxSelectors.getVBNXTSaveDiscardFlag);
    const isPreviewDetached = useSelector(reduxSelectors.isDocumentPreviewDetached);
    const isEditorDetached = useSelector(reduxSelectors.isEditorDetached);
    const canAddDocumentComments = useSelector(reduxSelectors.canAddDocumentComments);
    const canAddProcessComments = useSelector(reduxSelectors.canAddProcessComments);
    const selectedDocument = useSelector(reduxSelectors.getSelectedDocument);

    const {task, taskDetails, params, navigation, routerBlocker} = props;

    const [renderEditorInPortal, setRenderEditorInPortal] = useState(readEditorInPortal);
    const [handledWarningEnabled, setHandledWarningEnabled] = useState(true);
    const [enableModularViewResize, setEnableModularViewResize] = useState(document.body.clientWidth > MIN_SCREEN_WIDTH_FOR_RESIZE);
    const [width, setWidth] = useState<number>();
    // the resize handle is as tall as the grid, so the page draws again whenever the grid changes height
    const [, redrawForGridHeight] = useReducer((count: number) => count + 1, 0);

    const timeOnPageRef = useRef(Date.now());
    const resizeInstancesRef = useRef(0);
    const resizeHandlerRef = useRef<HTMLDivElement>(null);
    const gridRef = useRef<HTMLDivElement>(null);
    const editorPanelRef = useRef<HTMLDivElement>(null);
    const attachmentsPanelRef = useRef<HTMLDivElement>(null);
    const taskDetailsRef = useRef<HTMLDivElement>(null);

    const getPanelsVisibleForIntegration = () => {
        const applicationName = task.externalApplicationName || task.applicationTypeName || "";
        const docType = DOCUMENT_TYPE.asString(task.documentType);
        const integrationsForDocType = PANELS_BY_DOCTYPE_AND_INTEGRATION[docType];
        return integrationsForDocType ? (integrationsForDocType[applicationName] || PANELS_BY_DOCTYPE_AND_INTEGRATION.DEFAULT)
            : PANELS_BY_DOCTYPE_AND_INTEGRATION.DEFAULT;
    };

    const isEditorReadOnly = () => props.type === DETAILS_VIEW_TYPE.HISTORY_TASK
        || props.type === DETAILS_VIEW_TYPE.PROCESS
        || (props.type === DETAILS_VIEW_TYPE.DOCUMENT && props.activeCounter !== DOCUMENT_OVERVIEW_STATUS.BLOCKED);

    // The left side takes what the attachments and the gap leave, so the grid ends 16px from the right
    // edge as it starts 16px from the left. The classic page set both sides in px, which left room for
    // its sidebar and fell short of the grid by up to 32px here.
    const setRootStyle = (rightPanelWidth: number) => {
        const root = document.documentElement;
        root?.style.setProperty(
            "--columns_2_width_50_Template", "minmax(0, 1fr) " + rightPanelWidth + "px",
        );
        root?.style.setProperty(
            "--columns_3_width_46_Template", "minmax(0, 1fr) minmax(0, 1fr) " + rightPanelWidth + "px",
        );
    };

    const pushResizeEventToPanels = () => {
        eventBus.dispatch("@@grid/refreshGridLayout");
        eventBus.dispatch("@@grid/refreshAttachmentsPanelLayout");
    };

    const handleWindowResize = () => {
        if (document.body.clientWidth < MIN_SCREEN_WIDTH_FOR_RESIZE) {
            setEnableModularViewResize(false);
            return;
        }

        let attachmentPanelWidth = parseInt(window.localStorage.getItem(ATTACHMENTS_WIDTH_STORAGE_KEY)) || resizeHandlerRef.current?.clientWidth;
        const gridWidth = gridRef.current?.clientWidth;
        const otherWidth = gridWidth - attachmentPanelWidth - (SPACE_BETWEEN_PANELS * 2);

        if (otherWidth < LEFT_SIDE_MIN_WIDTH) {
            attachmentPanelWidth = gridWidth - LEFT_SIDE_MIN_WIDTH - (SPACE_BETWEEN_PANELS * 2);
        } else {
            attachmentPanelWidth = gridWidth / 2 - (SPACE_BETWEEN_PANELS * 2);
        }

        setWidth(attachmentPanelWidth);
        setEnableModularViewResize(true);
        setRootStyle(attachmentPanelWidth);
        pushResizeEventToPanels();
    };

    const handleArrowKeys = (key: string, numberOfAttachments: number) => {
        if (numberOfAttachments > 0) {
            let direction = 0;
            if (key === "E" || key === "e") {
                direction = 1;
            } else if (key === "Q" || key === "q") {
                direction = -1;
            }
            const currentIndex = selectedDocument?.index || 0;
            const newIndex = currentIndex + direction;

            if (newIndex >= 0 && newIndex < numberOfAttachments) {
                dispatch(selectedDocumentChanged(newIndex));
            }
        }
    };

    const handleNumericKeys = (key: string, numberOfAttachments: number) => {
        const computedIndex = parseInt(key, 10) - 1;
        if (computedIndex >= 0 && computedIndex < numberOfAttachments) {
            dispatch(selectedDocumentChanged(computedIndex));
        }
    };

    const handleKeyPress = (event: KeyboardEvent) => {
        event.stopPropagation();
        if (!canDo(event)) return;

        const numberOfAttachments = props.attachments?.length || 0;

        if (event.key >= '1' && event.key <= '9') {
            event.preventDefault();
            handleNumericKeys(event.key, numberOfAttachments);
        } else if (event.key === "e" || event.key === "E" || event.key === "q" || event.key === "Q") {
            event.preventDefault();
            handleArrowKeys(event.key, numberOfAttachments);
        } else {
            keyPressHandler(event, props.taskAction, [params.taskId], props.activeActions, routerBlocker?.history as () => void);
        }
    };

    const setDocumentPreviewTitle = () => {
        const documentPreviewTitle = translate("documentType." + DOCUMENT_TYPE.asString(task.documentType)) + " - " + navigation.shortTitle + " → " + navigation.companyName;
        dispatch(documentPreviewTitleChanged(documentPreviewTitle));
    };

    const setEditorTitle = () => {
        const editorTitle = translate("taskDetail.externalEditor") + " - " + navigation.shortTitle + " → " + navigation.companyName;
        dispatch(editorTitleChanged(editorTitle));
    };

    // the window listeners are added once, so they call the handlers of the latest render
    const keyPressRef = useRef(handleKeyPress);
    keyPressRef.current = handleKeyPress;
    const windowResizeRef = useRef(handleWindowResize);
    windowResizeRef.current = handleWindowResize;
    // read on unmount, when the store values of the last render are the ones that count
    const unmountStateRef = useRef({isEditorDetached, userConfiguration});
    unmountStateRef.current = {isEditorDetached, userConfiguration};

    useEffect(() => {
        const attachmentWidth = document.body.clientWidth < MIN_SCREEN_WIDTH_FOR_RESIZE
            ? gridRef.current?.clientWidth - 32
            : gridRef.current?.clientWidth * 0.5 - 48;
        timeOnPageRef.current = Date.now();
        resizeInstancesRef.current = 0;
        redrawForGridHeight();
        setWidth(parseInt(window.localStorage.getItem(ATTACHMENTS_WIDTH_STORAGE_KEY)) || attachmentWidth);
        if (document.body.clientWidth > MIN_SCREEN_WIDTH_FOR_RESIZE) {
            windowResizeRef.current();
        }

        const onKeyDown = (event: KeyboardEvent) => keyPressRef.current(event);
        const onWindowResize = () => windowResizeRef.current();
        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("resize", onWindowResize);

        const shouldEnforceDialog = taskDetails.skipRestart && taskDetails.reapproveType === CONFIRMATION;
        dispatch(approveMultipleShortcutKey(shouldEnforceDialog));
        dispatch(changeTryToLoopTasksFlag(true));
        dispatch(documentPreviewStatusChanged(DOCUMENT_PREVIEW_STATUS.AVAILABLE));
        setDocumentPreviewTitle();
        setEditorTitle();
        taskDetailsRef.current?.focus();

        return () => {
            dispatch(taskDetailsClosing());
            snowplowTrackEvent({
                category: "task_details",
                action: "resize",
                label: "resize_with_drag",
                value: String(resizeInstancesRef.current),
            }, unmountStateRef.current.userConfiguration);
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("resize", onWindowResize);
            dispatch(documentPreviewStatusChanged(DOCUMENT_PREVIEW_STATUS.WAITING));
            if (unmountStateRef.current.isEditorDetached) {
                dispatch(editorReattach());
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- set up once on mount, torn down on unmount
    }, []);

    const previousLoadingStatusRef = useRef(props.loadingStatus);
    useEffect(() => {
        if (previousLoadingStatusRef.current !== props.loadingStatus && props.loadingStatus === LOADING_STATUS.DONE) {
            const shouldEnforceDialog = taskDetails.skipRestart && props.docEditedFlag && taskDetails.reapproveType === CONFIRMATION;
            dispatch(approveMultipleShortcutKey(shouldEnforceDialog));
            if (!getPanelsVisibleForIntegration()[DOCUMENT_PREVIEW]) {
                dispatch(documentPreviewStatusChanged(DOCUMENT_PREVIEW_STATUS.UNAVAILABLE));
            }
        }
        previousLoadingStatusRef.current = props.loadingStatus;
        // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the attachments finish loading
    }, [props.loadingStatus]);

    // the page stays mounted when moving between tasks, so moving starts the page over for the next one
    const previousTaskIdRef = useRef(params.taskId);
    useEffect(() => {
        if (previousTaskIdRef.current === params.taskId) return;
        previousTaskIdRef.current = params.taskId;

        dispatch(documentPreviewStatusChanged(DOCUMENT_PREVIEW_STATUS.AVAILABLE));
        timeOnPageRef.current = Date.now();
        resizeInstancesRef.current = 0;
        setHandledWarningEnabled(true);
        setDocumentPreviewTitle();
        setEditorTitle();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when moving to another task
    }, [params.taskId]);

    const previousUnsavedChangesRef = useRef(iframeHasUnsavedChanges);
    useEffect(() => {
        if (previousUnsavedChangesRef.current === iframeHasUnsavedChanges) return;
        previousUnsavedChangesRef.current = iframeHasUnsavedChanges;
        routerBlocker?.shouldBlockNavigation?.(iframeHasUnsavedChanges);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the editor's unsaved changes come or go
    }, [iframeHasUnsavedChanges]);

    useEffect(() => {
        if (taskActionsRunning && (actionParams?.type === TASK_ACTIONS.APPROVE || actionParams?.type === TASK_ACTIONS.REJECT)) {
            snowplowTrackEvent({
                category: "task_details",
                action: "task_details",
                label: "task_details_time_on_page",
                value: String((Date.now() - timeOnPageRef.current) / 1000),
            }, userConfiguration);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- logged when an approve or reject starts
    }, [taskActionsRunning, actionParams?.type]);

    const remain = () => {
        routerBlocker?.blocker?.reset?.();
    };

    const onRenderInPortalChange = (value: boolean) => {
        setRenderEditorInPortal(value);
        window.localStorage.setItem(EDITOR_PORTAL_STORAGE_KEY, String(value));
    };

    // the editor's new size is only known once it has been drawn
    const isFirstPortalRenderRef = useRef(true);
    useEffect(() => {
        if (isFirstPortalRenderRef.current) {
            isFirstPortalRenderRef.current = false;
            return;
        }
        pushResizeEventToPanels();
        redrawForGridHeight();
    }, [renderEditorInPortal]);

    const onResize = (_event: unknown, {size}: { size: { width: number } }) => {
        const attachmentsPanelWidth = size.width || resizeHandlerRef.current?.clientWidth;
        const gridWidth = gridRef.current?.clientWidth - (2 * SPACE_BETWEEN_PANELS);
        const otherWidth = gridWidth - attachmentsPanelWidth - SPACE_BETWEEN_PANELS;
        if (otherWidth > LEFT_SIDE_MIN_WIDTH) {
            setWidth(attachmentsPanelWidth);
            setRootStyle(attachmentsPanelWidth);
        }
    };

    const onResizeStop = () => {
        window.localStorage.setItem(ATTACHMENTS_WIDTH_STORAGE_KEY, String(width));
        editorPanelRef.current?.classList.remove("no-pointer-events");
        attachmentsPanelRef.current?.classList.remove("no-pointer-events");
        resizeInstancesRef.current = resizeInstancesRef.current + 1;
        pushResizeEventToPanels();
    };

    const onResizeStart = () => {
        editorPanelRef.current?.classList.add("no-pointer-events");
        attachmentsPanelRef.current?.classList.add("no-pointer-events");
    };

    const isAttachmentPanelWidthDefault = () => {
        const gridTotalWidth = gridRef.current?.clientWidth - 10;
        const defaultAttPanelWidth = gridTotalWidth * 0.5;
        const attPanelWidth = resizeHandlerRef.current?.clientWidth;

        return attPanelWidth >= defaultAttPanelWidth - 50 && attPanelWidth <= defaultAttPanelWidth + 50;
    };

    const isAttachmentPanelMaxWidth = () => {
        const gridTotalWidth = gridRef.current?.clientWidth - 10;
        const attPanelWidth = resizeHandlerRef.current?.clientWidth;
        const leftPanelWidth = gridTotalWidth - attPanelWidth;

        return leftPanelWidth >= LEFT_SIDE_MIN_WIDTH - 50 && leftPanelWidth <= LEFT_SIDE_MIN_WIDTH + 50;
    };

    // maximize and minimize tell the panels once the new width has been drawn; a drag tells them when it stops
    const pushResizeOnWidthChangeRef = useRef(false);
    useEffect(() => {
        if (!pushResizeOnWidthChangeRef.current) return;
        pushResizeOnWidthChangeRef.current = false;
        pushResizeEventToPanels();
    }, [width]);

    const setAttachmentsPanelWidth = (attachmentPanelWidth: number) => {
        if (attachmentPanelWidth === width) {
            pushResizeEventToPanels();
        } else {
            pushResizeOnWidthChangeRef.current = true;
            setWidth(attachmentPanelWidth);
        }
        window.localStorage.setItem(ATTACHMENTS_WIDTH_STORAGE_KEY, String(attachmentPanelWidth));
        setRootStyle(attachmentPanelWidth);
    };

    const maximizeAttachmentsPanel = () => {
        const gridWidth = gridRef.current?.clientWidth - (3 * SPACE_BETWEEN_PANELS);
        setAttachmentsPanelWidth(gridWidth - LEFT_SIDE_MIN_WIDTH);
    };

    const minimizeAttachmentsPanel = () => {
        if (!isAttachmentPanelWidthDefault()) {
            const gridWidth = gridRef.current?.clientWidth - (3 * SPACE_BETWEEN_PANELS);
            setAttachmentsPanelWidth(gridWidth * 0.5);
        }
    };

    const sendMessageToEditor = (command: string) => {
        snowplowTrackEvent({
            category: "task_details",
            action: "forgot_to_save_changes",
            label: "forgot_to_save_changes_in_editor",
            value: command,
        }, userConfiguration);

        sendEditorCommand({command});
    };

    const saveChanges = () => {
        if (iframeHasUnsavedChanges) {
            sendMessageToEditor("saveChanges");
        }
    };

    const discardChanges = () => {
        if (iframeHasUnsavedChanges) {
            sendMessageToEditor("discardChanges");
        }
    };

    const isHandled = () => {
        const currentStatus = DETAILS_STATUS[workflowData?.status];
        return props.allowActionlessComments &&
            currentStatus !== DETAILS_STATUS.ACTIVE &&
            currentStatus !== DETAILS_STATUS.BLOCKED;
    };

    const readOnlyComments = () =>
        (props.allowActionlessComments ? isHandled() : props.isHistory || props.isHandled) ||
        (props.isDocument && !canAddDocumentComments) ||
        (props.isHistory && !canAddProcessComments);

    const editorReadOnly = isEditorReadOnly();
    const areActionCommentsVisible = props.allowActionlessComments ? true : !props.isDocument;
    const panelsVisibleForIntegration = getPanelsVisibleForIntegration();

    // with no sidebar to hide them, every panel the integration offers is shown
    const panelsVisible = {
        [WORKFLOW_DETAILS]: panelsVisibleForIntegration[WORKFLOW_DETAILS],
        [DOCUMENT_DETAILS]: panelsVisibleForIntegration[DOCUMENT_DETAILS],
        [DOCUMENT_PREVIEW]: isPreviewDetached ? false : panelsVisibleForIntegration[DOCUMENT_PREVIEW],
        [COMMENTS]: panelsVisibleForIntegration[COMMENTS] && areActionCommentsVisible,
        [EDITOR]: panelsVisibleForIntegration[EDITOR],
        [ACCOUNTING_GRID]: panelsVisibleForIntegration[ACCOUNTING_GRID],
    };
    const isAnyDetailPanelVisible = panelsVisible[COMMENTS] || panelsVisible[WORKFLOW_DETAILS] || panelsVisible[DOCUMENT_DETAILS];

    const accountingGridVisible = task?.hasAccountingGrid || task?.allowGridEdit || taskDetails?.allowGridEdit;
    const editorVisibleInline = !isEditorDetached && (taskDetails.externalEditorAvailable || accountingGridVisible);
    const containerClass = getGridContainerClass(panelsVisible, editorVisibleInline, renderEditorInPortal);

    // the classic panels read the page's props, so they are handed what the classic page had
    const panelProps = {
        ...props,
        translate,
        translationKeyExists,
        workflowData,
        userLanguage,
        documentData,
        taskActionsRunning,
        actionParams,
        userConfiguration,
        commentsCount,
        iframeHasUnsavedChanges,
        iframeVBNXTSaveDiscardFlag,
        isPreviewDetached,
        isEditorDetached,
        canAddDocumentComments,
        canAddProcessComments,
        selectedDocument,
    };

    const attachmentsPanelProps = {
        attachments: props.attachments,
        loadingStatus: props.loadingStatus,
        taskDetails,
        translate,
        type: props.type,
        isFullWidth: isAnyDetailPanelVisible,
    };

    return (
        <div>
            <DocumentPreviewWindow onKeyPress={handleKeyPress} translate={translate}/>
            <DocumentEditorWindow {...panelProps}
                                  onKeyPress={handleKeyPress}
                                  editorReadOnly={editorReadOnly}
                                  onRenderInPortalChange={onRenderInPortalChange}/>
            <div id="task-details-wrapper" className="task-details-redesign" tabIndex={-1} ref={taskDetailsRef}>
                <AsyncTaskActionExecutorTask
                    translate={translate}
                    type={props.type}
                    taskDetails={taskDetails}
                    navigateBetweenTasks={props.navigateBetweenTasks}
                    reloadTasks={() => dispatch(loadTasks())}
                />

                {routerBlocker?.isNavigationBlocked?.() &&
                    <NavigationConfirmTrigger
                        translate={translate}
                        iframeHasUnsavedChanges={iframeHasUnsavedChanges}
                        enableSaveDiscard={iframeHasUnsavedChanges && iframeVBNXTSaveDiscardFlag}
                        remain={remain}
                        saveChanges={saveChanges}
                        discardChanges={discardChanges}
                    />
                }

                {task.isHandled && handledWarningEnabled &&
                    <HandledTaskTrigger closeCallback={() => setHandledWarningEnabled(false)}/>
                }

                <TaskHeader params={params}
                            task={task}
                            taskDetails={taskDetails}
                            activeActions={props.activeActions}
                            taskAction={props.taskAction}
                            navigation={navigation}
                            navigateBetweenTasks={props.navigateBetweenTasks}
                            navigateBack={props.navigateBack}
                            navigateBackPath={props.navigateBackPath}
                            editorReadOnly={editorReadOnly}
                            type={props.type}/>

                <div className="grid-layout-wrapper">
                    <div className="grid-panel-wrapper">
                        <div ref={gridRef}
                             className={containerClass + " grid-base "} data-cy="taskDetail.gridBase">
                            <div className={"grid-panel-wrapper blue attachments-panel"} ref={resizeHandlerRef}>
                                <div className={"attachments-panel__grid-panel grid-panel"}>
                                    <PanelErrorBoundary panel="attachments">
                                        {enableModularViewResize ?
                                            <ResizableBox
                                                width={width}
                                                style={{height: "100%"}}
                                                maxConstraints={[gridRef.current?.clientWidth - LEFT_SIDE_MIN_WIDTH, LEFT_SIDE_MIN_WIDTH]}
                                                minConstraints={[LEFT_SIDE_MIN_WIDTH, LEFT_SIDE_MIN_WIDTH]}
                                                resizeHandles={["w"]}
                                                handle={<ResizeHandle translate={translate}
                                                                      height={gridRef.current?.clientHeight}/>}
                                                draggableOpts={{grid: [25, 25]}}
                                                onResize={onResize}
                                                onResizeStop={onResizeStop}
                                                onResizeStart={onResizeStart}>
                                                <div ref={attachmentsPanelRef} className={"h-100 grid-panel"}>
                                                    <AttachmentsPanel {...attachmentsPanelProps}
                                                                      width={width}
                                                                      maximizePanel={maximizeAttachmentsPanel}
                                                                      minimizePanel={minimizeAttachmentsPanel}
                                                                      isAttachmentPanelWidthDefault={isAttachmentPanelWidthDefault()}
                                                                      isAttachmentPanelMaxWidth={isAttachmentPanelMaxWidth()}
                                                    />
                                                </div>
                                            </ResizableBox> :
                                            <AttachmentsPanel {...attachmentsPanelProps}/>
                                        }
                                    </PanelErrorBoundary>
                                </div>
                            </div>

                            {taskDetails.externalEditorAvailable && !isEditorDetached &&
                                <div className={"editor" + (renderEditorInPortal ? " small" : "")}
                                     ref={editorPanelRef}>
                                    <PanelErrorBoundary panel="documentEditor">
                                        <DocumentEditorPanel {...panelProps}
                                                             renderInPortal={renderEditorInPortal}
                                                             editorReadOnly={editorReadOnly}
                                                             onRenderInPortalChange={onRenderInPortalChange}/>
                                    </PanelErrorBoundary>
                                </div>
                            }

                            <div className="doc-data">
                                <PanelErrorBoundary panel="documentData">
                                    <DocumentData taskId={params.taskId}
                                                  documentType={task.documentType}
                                                  readOnly={props.readOnly}
                                                  isDocument={props.isDocument}/>
                                </PanelErrorBoundary>
                            </div>

                            <div className="workflow-details">
                                <PanelErrorBoundary panel="workflowDetails">
                                    <WorkflowDetailsPanel taskId={params.taskId}
                                                          isDocument={props.isDocument}
                                                          taskDetails={taskDetails}
                                                          documentDueDate={task?.documentDueDate}
                                                          taskDueDate={task?.dueDate}/>
                                </PanelErrorBoundary>
                            </div>

                            <div className="comments">
                                <PanelErrorBoundary panel="comments">
                                    <DraftAndComments taskId={params.taskId}
                                                      uid={task.uid}
                                                      documentUid={task.documentUid ? task.documentUid : task.uidDocument}
                                                      readOnly={readOnlyComments()}
                                                      initialCommentsCount={task.numberOfComments || taskDetails.numberOfComments}
                                                      addCommentHandler={props.addCommentHandler}
                                                      getCommentsHandler={props.getCommentsHandler}
                                                      allowExtraComments={!!(props.isDocument || props.allowActionlessComments)}/>
                                </PanelErrorBoundary>
                            </div>

                            {accountingGridVisible && !isEditorDetached &&
                                <div className={"accounting-grid-panel"}>
                                    <PanelErrorBoundary panel="accountingGrid">
                                        <AccountingGridPanel {...panelProps}
                                                             renderInPortal={renderEditorInPortal}
                                                             onRenderInPortalChange={onRenderInPortalChange}/>
                                    </PanelErrorBoundary>
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TaskDetails;
